import React, {PropsWithChildren, useEffect, useMemo, useRef, useState} from "react";
import {
  chatConfigSelector,
  chatTokenSelector,
  useGetChatConfigQuery,
  useGetChatTokenQuery
} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAuth} from "../AuthProvider/AuthProvider";
import SendbirdProvider from "@sendbird/uikit-react/SendbirdProvider";
import {useSendbird} from "@sendbird/uikit-react";
import {GroupChannelListProvider, useGroupChannelList} from "@sendbird/uikit-react/GroupChannelList/context";
import {UseSendbirdResult} from "../../typings/sendbird-uikit-react";
import {chatTheme} from "./chat-theme";

export interface ChatContextValue {
  enabled: boolean;
  sendbird?: UseSendbirdResult
  unread: number
}

type SendbirdConfig = {
  appId: string;
  userId: string;
  token: string;
}

export const ChatContext = React.createContext<ChatContextValue>({
  enabled: false,
  sendbird: undefined,
  unread: 0,
});

export const useChat = () => React.useContext(ChatContext);

export const ChatProvider = (props: PropsWithChildren) => {

  const {children} = props;

  const [sendbirdConfig, setSendbirdConfig] = useState<SendbirdConfig | undefined>(undefined!);

  const {userId} = useAuth()

  const selectChatConfig = useMemo(() => {
    return chatConfigSelector();
  }, [])

  const selectChatToken = useMemo(() => {
    return chatTokenSelector();
  }, [])

  const {chatEnabled, sendbirdAppId} = useGetChatConfigQuery(userId ? undefined : skipToken, {
    selectFromResult: (result) => {
      const response = selectChatConfig(result)
      return {
        ...result,
        chatEnabled: response?.enabled ?? false,
        sendbirdAppId: response?.sendbirdAppId,
      }
    }
  })

  const {sendbirdToken, sendbirdUserId} = useGetChatTokenQuery((userId && chatEnabled) ? undefined : skipToken, {
    selectFromResult: (result) => {
      const response = selectChatToken(result)
      return {
        ...result,
        sendbirdToken: response?.token,
        sendbirdUserId: response?.sendbirdUserId
      }
    }
  })

  useEffect(() => {
    if (chatEnabled && sendbirdAppId && sendbirdToken && sendbirdUserId) {
      setSendbirdConfig({
        appId: sendbirdAppId,
        userId: sendbirdUserId,
        token: sendbirdToken
      })
    } else {
      setSendbirdConfig(undefined)
    }
  }, [chatEnabled, sendbirdAppId, sendbirdToken, sendbirdUserId])

  if (!sendbirdConfig) {
    return (
      <ChatContext.Provider value={{enabled: false, sendbird: undefined, unread: 0}}>
        {children}
      </ChatContext.Provider>
    )
  }

  return (
    <SendbirdProvider appId={sendbirdConfig.appId} userId={sendbirdConfig.userId} accessToken={sendbirdConfig.token} colorSet={chatTheme}>
      <GroupChannelListProvider
        className="sendbird-app__group-chanel-list-none"
        onChannelCreated={() => {}}
        onChannelSelect={() => {}}>
        <InnerChatProvider>
          {children}
        </InnerChatProvider>
      </GroupChannelListProvider>
    </SendbirdProvider>
  )
}

const InnerChatProvider = (props: PropsWithChildren) => {
  const {children} = props;

  const sendbird = useSendbird()
  const groupChannelList = useGroupChannelList()

  const unread = groupChannelList.state.groupChannels.reduce((sum, ch) => sum + (ch.unreadMessageCount ?? 0), 0)
  const prevUnread = useRef<number>(unread)

  const baseTitle = useRef(document.title)

  useEffect(() => {
    if (unread > 0) {
      document.title = `(${unread}) ${baseTitle.current}`
    } else {
      document.title = baseTitle.current
    }

    if (unread > prevUnread.current) {
      new Audio('/chat_notification.mp3').play().catch(console.warn)
    }

    prevUnread.current = unread
  }, [unread])

  return (
    <ChatContext.Provider
      value={{
        enabled: true,
        sendbird: sendbird,
        unread,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}
